import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Check, CloudOff, FilterX, RotateCcw, Sparkles } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { useCalendarViewStore, type PhoneCalendarLayout } from "@/stores/calendarViewStore";
import type { BrandCalendarSettings, CalendarPlanSlot } from "@/types/contentCalendar.type";
import type { BriefRailInfo } from "@/types/productionBrief.type";
import { cn } from "@/utils/helpers";
import CalendarSettingsModal from "../components/CalendarSettingsModal";
import DeleteSlotModal from "../components/DeleteSlotModal";
import { clientLabel, clientTitle, shortDate, type PrimaryAction } from "../postState";
import ElapsedSince from "../ElapsedSince";
import { useStuckClock } from "../useStuckClock";
import { anyMachineWork, slotDeleteBlocked } from "../slotStage";
import { awaitsPick } from "../concepts/ideaCardModel";
import { agentRail } from "../../agent/agentFocusStore";
import AddPostChatCard from "./AddPostChatCard";
import AddPostDialog from "./AddPostDialog";
import CadenceDialog, { type CadenceDraft } from "./CadenceDialog";
import CalendarHeader, { type CalendarViewMode } from "./CalendarHeader";
import ScheduleView from "./ScheduleView";
import type { UpcomingPost } from "../api/useScheduling";
import CalendarScope from "./CalendarScope";
import { CentreCard, CentreMessage, ScopeLine, stateLink, statePrimary, stateSecondary } from "./CalendarStates";
import {
  CHANNEL_NAME,
  MONTH_NAMES,
  NO_FILTERS,
  compactMonthDays,
  dayKey,
  dayMonthShort,
  defaultChosenDay,
  defaultWeekStart,
  filterCounts,
  filteredSentence,
  filtersActive,
  matchesFilters,
  monthBefore,
  phoneSchedule,
  pillarIndexMap,
  postsByDay,
  readPosts,
  scheduleStart,
  scopeLines,
  scopeSummaryLines,
  autoPlanRun,
  closedSentence,
  sixWeekGrid,
  statusSentence,
  todayUtc as todayOf,
  weekStartOf,
  weekTitle,
  weekTouchesMonth,
  type CalendarFilters,
  type CalendarPost,
  type DayCell,
  type MonthTargets,
  type PillarRef,
} from "./calendarModel";
import MonthGrid, { type Lift, type MoveRequest, type NewPost } from "./MonthGrid";
import type { PeekIntent } from "./CalendarPostCard";
import { createPeekController } from "./peekIntent";
import MonthList, { ListHeader, type RiskDecision } from "./MonthList";
import { EmptyState, StateAction } from "../../components/ViewStates";
import MovePostDialog from "./MovePostDialog";
import MonthPickerSheet from "./MonthPickerSheet";
import PlanReviewStrip from "./PlanReviewStrip";
import {
  CalendarTopLane,
  CompactMonth,
  PhoneDayRow,
  PhoneGroupHeader,
  PhoneLargeTitle,
  PhoneSchedule,
  PhoneScheduleSkeleton,
  PhoneStateCard,
  PostActionsSheet,
  phonePrimary,
  phoneSecondary,
} from "./PhoneCalendar";
import { useCalendarViewport } from "./CalendarSheet";
import PostPeek, { FOCUSABLE, type PeekContent } from "./PostPeek";
import WeekGrid from "./WeekGrid";

export type { NewPost } from "./MonthGrid";
export type { CalendarViewMode } from "./CalendarHeader";

/** Where the month stands, which decides what the body shows. */
export type MonthState =
  | "ready"
  | "loading"
  | "error"
  | "not_planned"
  | "planning"
  | "planning_failed"
  | "no_strategy";

const DAY_MS = 86_400_000;
/** The rail's add-post card belongs to the month view. */
const ADD_POST_KEY = "calendar:add-post";

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

/**
 * One month of a brand's plan: the header, then Month, Week or List, and the
 * month's scope along the foot (DESIGN.md "The Calendar in Parts").
 *
 * Every post reads its stage and whose turn it is from postState, so the card,
 * the peek, the filters and the status line all say the same words as the post
 * page. Hover (after a 300ms intent delay) or keyboard focus peeks; a click
 * opens the post. Touch has no hover: a tap opens the post.
 */
export default function MonthView({
  year,
  month,
  state = "ready",
  slots,
  neighbourSlots = [],
  queueSlots = [],
  queueLive = false,
  onQueueWanted,
  targets,
  briefBySlot,
  brandId = null,
  clientName = null,
  canEdit = true,
  closed = false,
  pillars,
  settings,
  hasCustomCadence = false,
  savingCadence,
  onSaveCadence,
  onResetCadence,
  onReplan,
  view: viewProp,
  onViewChange,
  monthsWithPosts,
  onOpenSlot,
  onPickIdeasWalk,
  onOpenProposals,
  onPrevMonth,
  onNextMonth,
  onPickMonth,
  onSeeYear,
  onMoveSlot,
  onAddPost,
  onAddQuickPost,
  onQuickPost,
  onDeleteSlot,
  peekAction,
  menuItems = [],
  notices,
  planningSince = null,
  planError = null,
  onPlan,
  planPending = false,
  onStopPlanning,
  onRetry,
  staleSince = null,
  onDecideRisk,
  notificationBell = null,
  planId = null,
  plannedAt = null,
  strategyHref = "/dashboard/strategy",
  onOpenScheduled,
}: {
  /** Schedule: open a post going live (its publishing step). */
  onOpenScheduled?: (post: UpcomingPost) => void;
  year: number;
  /** 1-12. */
  month: number;
  state?: MonthState;
  slots: CalendarPlanSlot[];
  /** The months either side, read only, for the week's days outside this month. */
  neighbourSlots?: CalendarPlanSlot[];
  /** Other months' posts, for the Needs you queue in List. */
  queueSlots?: CalendarPlanSlot[];
  /** The host reads other months for the queue. Without it, Needs you in List stays on this month. */
  queueLive?: boolean;
  /** Told when List with Needs you is on or off, so other months are read only then. */
  onQueueWanted?: (wanted: boolean) => void;
  /** The mix this month was claimed for, off its own plan row. */
  targets: MonthTargets | null;
  briefBySlot?: Map<string, BriefRailInfo>;
  brandId?: string | null;
  /** The brand's client, for whose-turn words. */
  clientName?: string | null;
  canEdit?: boolean;
  /** A closed or past month: read only, no plus on days. */
  closed?: boolean;
  /** The active strategy's pillars, in strategy order. */
  pillars?: PillarRef[];
  settings?: BrandCalendarSettings | null;
  hasCustomCadence?: boolean;
  savingCadence?: boolean;
  onSaveCadence?: (draft: CadenceDraft) => Promise<void> | void;
  onResetCadence?: () => Promise<void> | void;
  onReplan?: (changedCadence: CadenceDraft | null) => void;
  view?: CalendarViewMode;
  onViewChange?: (view: CalendarViewMode) => void;
  /** "2026-12" for months with posts, for the month picker's dots. */
  monthsWithPosts?: ReadonlySet<string>;
  onOpenSlot: (slot: CalendarPlanSlot) => void;
  /** "Pick ideas for 3 posts": open the first post waiting for a pick, walking post to post. */
  onPickIdeasWalk?: (slot: CalendarPlanSlot) => void;
  /** Opens Client proposals (the status line's "2 are with Fatafeat"). */
  onOpenProposals?: () => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onPickMonth?: (year: number, month: number) => void;
  /** Zoom out to the year. */
  onSeeYear?: () => void;
  onMoveSlot?: (move: MoveRequest) => Promise<void> | void;
  onAddPost?: (post: NewPost) => Promise<void> | void;
  /** Creates a quick post (straight to production) from the chat card's fields. */
  onAddQuickPost?: (post: NewPost) => Promise<void> | void;
  /** The quick post dialog, where the chat card cannot create one itself. */
  onQuickPost?: (day: number) => void;
  onDeleteSlot?: (slotId: string) => Promise<void> | void;
  /** The peek's one action, for the postState primaries the host can run from here. */
  peekAction?: {
    canRun: (action: PrimaryAction, slot: CalendarPlanSlot) => boolean;
    run: (action: PrimaryAction, slot: CalendarPlanSlot) => void;
  };
  /** More entries for the settings menu (guests, client proposals). */
  menuItems?: { label: string; onPress: () => void }[];
  notices?: ReactNode;
  /** When planning started, for "0:48 so far". */
  planningSince?: string | null;
  planError?: string | null;
  onPlan?: () => void;
  planPending?: boolean;
  /** Stop planning (Figma 08, 13 Oct): the run's result is thrown away. */
  onStopPlanning?: () => void;
  onRetry?: () => void;
  /**
   * The month's last good read, when a refresh failed and the posts on screen
   * may be out of date (Pen: Month · could not load). The page keeps them and
   * says so in a status banner.
   */
  staleSince?: number | null;
  /** Accept or dismiss a post's at-risk suggestion (backend #126). */
  onDecideRisk?: RiskDecision;
  /** The bell in the phone's top lane (a bare 44px button). */
  notificationBell?: ReactNode;
  /** The month's plan and when it landed, for the plan review strip. */
  planId?: string | null;
  plannedAt?: string | null;
  strategyHref?: string;
}) {
  const navigate = useNavigate();
  const { isGuest } = usePermissions();
  const monthName = MONTH_NAMES[month - 1];
  // The clock ticks only while a post is in a machine state (planning, writing).
  const now = useStuckClock(anyMachineWork([...slots, ...queueSlots], briefBySlot));
  const today = todayOf(now);

  const viewport = useCalendarViewport();
  const phone = viewport === "phone";
  const tablet = viewport === "tablet";
  const span = tablet ? 3 : 7;
  const [ownView, setOwnView] = useState<CalendarViewMode>("month");
  // A phone has two views of its own, List first (the calendar contract): a
  // schedule running across months, and the month as a grid with a day's posts.
  // Remembered apart from the desk's Month, Week or List, so the phone opens on
  // the view it was last left in.
  const phoneView = useCalendarViewStore((store) => store.phoneLayout);
  const setPhoneView = useCalendarViewStore((store) => store.setPhoneLayout);
  const view: CalendarViewMode = phone ? phoneView : (viewProp ?? ownView);
  const setView = useCallback(
    (next: CalendarViewMode) => {
      setOwnView(next);
      onViewChange?.(next);
    },
    [onViewChange],
  );

  const [filters, setFilters] = useState<CalendarFilters>(NO_FILTERS);
  const [lift, setLift] = useState<Lift | null>(null);
  const [peek, setPeek] = useState<PeekContent | null>(null);
  const peekAnchor = useRef<HTMLElement | null>(null);
  const [adding, setAdding] = useState<number | null>(null);
  const [moving, setMoving] = useState<CalendarPlanSlot | null>(null);
  const [deleting, setDeleting] = useState<CalendarPlanSlot | null>(null);
  const [editingScope, setEditingScope] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [actionsFor, setActionsFor] = useState<CalendarPost | null>(null);
  const monthKey = `${year}-${month}`;
  // Where the phone's schedule starts, and the day the phone's Month has open.
  // Both belong to the month they were set on, so a new month starts afresh.
  const [scheduleFromState, setScheduleFrom] = useState<{ key: string; utc: number } | null>(null);
  const [chosenState, setChosen] = useState<{ key: string; day: number } | null>(null);
  const focusedDay = useRef<DayCell | null>(null);

  // The week in Week view: today's week when today is in this month, else its first.
  const [weekStart, setWeekStart] = useState(() => defaultWeekStart(year, month, now));
  // A month or span change re-seats the week during render (no effect, no extra pass).
  const [weekSeed, setWeekSeed] = useState({ year, month, span });
  if (weekSeed.year !== year || weekSeed.month !== month || weekSeed.span !== span) {
    setWeekSeed({ year, month, span });
    if (!weekTouchesMonth(weekStart, year, month, span)) setWeekStart(defaultWeekStart(year, month, now));
  }
  // The latest week for the arrows, so two quick presses move two weeks.
  const weekRef = useRef(weekStart);
  useEffect(() => {
    weekRef.current = weekStart;
  }, [weekStart]);

  const pillarIndex = useMemo(
    () => pillarIndexMap(pillars ?? [], [...slots, ...neighbourSlots, ...queueSlots].map((slot) => slot.pillarId)),
    [pillars, slots, neighbourSlots, queueSlots],
  );
  const readOptions = useMemo(
    () => ({ briefBySlot, clientName, isGuest, now, pillarIndex }),
    [briefBySlot, clientName, isGuest, now, pillarIndex],
  );
  const posts = useMemo(() => readPosts(slots, readOptions), [slots, readOptions]);
  const neighbourPosts = useMemo(() => readPosts(neighbourSlots, readOptions), [neighbourSlots, readOptions]);
  const queuePosts = useMemo(() => readPosts(queueSlots, readOptions), [queueSlots, readOptions]);
  const counts = useMemo(() => filterCounts(posts, pillars ?? []), [posts, pillars]);
  const matched = useMemo(() => posts.filter((post) => matchesFilters(post, filters)), [posts, filters]);
  const active = filtersActive(filters);
  // A planned month is held to its own targets; an unplanned one reads the brand's cadence.
  const monthTargets = useMemo<MonthTargets | null>(
    () =>
      targets ??
      (settings
        ? {
            static: settings.staticPostsPerMonth,
            vertical: settings.verticalVideoPostsPerMonth,
            horizontal: settings.horizontalVideoPostsPerMonth,
          }
        : null),
    [targets, settings],
  );
  const scope = useMemo(
    () => scopeLines(slots, monthTargets, { countShort: state === "ready" }),
    [slots, monthTargets, state],
  );

  const canWrite = canEdit && !closed;
  const canAdd = canWrite && !!onAddPost && (state === "ready" || state === "not_planned" || state === "planning_failed");
  const other = isGuest ? "the team" : clientLabel(clientName);
  const Other = isGuest ? "The team" : clientTitle(clientName);

  // ---------------------------------------------------------------- navigation
  const goToday = useCallback(() => {
    const d = new Date(Date.now());
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    setWeekStart(weekStartOf(Date.now()));
    if ((y !== year || m !== month) && onPickMonth) onPickMonth(y, m);
  }, [month, onPickMonth, year]);

  const step = useCallback(
    (direction: 1 | -1) => {
      if (view !== "week") {
        if (direction === 1) onNextMonth();
        else onPrevMonth();
        return;
      }
      // A tablet's week is three days at a time (Pen "Decided · Tablet week · B", Emad 3 Oct).
      const next = weekRef.current + direction * span * DAY_MS;
      weekRef.current = next;
      setWeekStart(next);
      if (!weekTouchesMonth(next, year, month, span)) {
        if (direction === 1) onNextMonth();
        else onPrevMonth();
      }
    },
    [month, onNextMonth, onPrevMonth, span, view, year],
  );

  // --------------------------------------------------------------------- peek
  // A card peeks on hover (after the intent delay) or keyboard focus, non-modal;
  // a click opens the post. A day's "+n more" list is a click, and modal.
  const [peekMode, setPeekMode] = useState<"hover" | "click">("click");
  const peekNow = useRef({ open: false, mode: peekMode });
  useEffect(() => {
    peekNow.current = { open: !!peek, mode: peekMode };
  }, [peek, peekMode]);
  // The peek as shown (the latest read of its post), for Tab into it.
  const livePeekRef = useRef<PeekContent | null>(null);
  const showPeek = useCallback((post: CalendarPost, anchor: HTMLElement) => {
    peekAnchor.current = anchor;
    setPeekMode("hover");
    setPeek({ kind: "post", post });
  }, []);
  const [peekIntent] = useState(() =>
    createPeekController<CalendarPost, HTMLElement>({
      timers: { set: (run, ms) => window.setTimeout(run, ms), clear: (id) => window.clearTimeout(id) },
      show: showPeek,
      close: () => {
        if (peekNow.current.mode === "hover") setPeek(null);
      },
      modalOpen: () => peekNow.current.open && peekNow.current.mode === "click",
    }),
  );
  useEffect(() => () => peekIntent.dispose(), [peekIntent]);
  const peekFor = useCallback(
    (post: CalendarPost): PeekIntent => ({
      enter: (anchor) => peekIntent.enter(post, anchor),
      leave: () => peekIntent.leave(),
      focus: (anchor) => peekIntent.focus(post, anchor),
      blur: (next) => peekIntent.blur(!!next?.closest("[data-calendar-peek]")),
      intoPeek: () => {
        const open = peekNow.current;
        const shown = livePeekRef.current;
        if (!open.open || open.mode !== "hover" || shown?.kind !== "post" || shown.post.slot.id !== post.slot.id) return false;
        const first = document.querySelector<HTMLElement>(`[data-calendar-peek] :is(${FOCUSABLE})`);
        if (!first) return false;
        first.focus();
        return true;
      },
    }),
    [peekIntent],
  );
  const openPost = useCallback(
    (post: CalendarPost) => {
      peekIntent.dispose();
      setPeek(null);
      onOpenSlot(post.slot);
    },
    [peekIntent, onOpenSlot],
  );
  const openDay = useCallback((cell: DayCell, dayPosts: CalendarPost[], anchor: HTMLElement) => {
    peekIntent.dispose();
    peekAnchor.current = anchor;
    setPeekMode("click");
    setPeek({ kind: "day", cell, posts: dayPosts });
  }, [peekIntent]);

  // Keep the open peek on the post's latest read after a refetch.
  const livePeek: PeekContent | null = useMemo(() => {
    if (!peek || peek.kind !== "post") return peek;
    const fresh = [...posts, ...queuePosts, ...neighbourPosts].find((post) => post.slot.id === peek.post.slot.id);
    return fresh ? { kind: "post", post: fresh } : null;
  }, [peek, posts, queuePosts, neighbourPosts]);
  // Written after render (the handlers read it on events, never during a render).
  useEffect(() => {
    livePeekRef.current = livePeek;
  }, [livePeek]);

  const ownMonth = (slot: CalendarPlanSlot) => {
    const d = new Date(slot.scheduledAt);
    return d.getUTCFullYear() === year && d.getUTCMonth() + 1 === month;
  };

  const primaryFor = (action: PrimaryAction, post: CalendarPost) => {
    if (!canWrite || !ownMonth(post.slot)) return false;
    if (action === "move-post") return !!onMoveSlot;
    return peekAction?.canRun(action, post.slot) ?? false;
  };
  const runPrimary = (action: PrimaryAction, post: CalendarPost) => {
    setPeek(null);
    if (action === "move-post") setMoving(post.slot);
    else peekAction?.run(action, post.slot);
  };

  // ------------------------------------------------------------------ adding
  // Adding a post happens in the side chat: the day's + (and N) opens the rail
  // on a two-step card, rendered locally with no model call. Where no rail is
  // mounted to take it, the add dialog stands in.
  const addInChat = useRef<(day: number) => void>(() => undefined);
  useEffect(() => {
    addInChat.current = (day: number) => {
    if (!onAddPost) return;
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    const taken = agentRail.ask({
      key: ADD_POST_KEY,
      prompt: `Add a post on ${dayMonthShort({ weekday, day, month })}`,
      card: (done) => (
        <AddPostChatCard
          year={year}
          month={month}
          day={day}
          pillars={pillars}
          onAdd={onAddPost}
          onAddQuick={onAddQuickPost}
          onQuickDialog={onQuickPost}
          onDone={done}
        />
      ),
    });
    if (!taken) setAdding(day);
    };
  });
  useEffect(() => () => agentRail.clearAsk(ADD_POST_KEY), []);
  const addOn = useCallback(
    (cell: DayCell | null) => {
      if (!canAdd) return;
      const target = cell && cell.inMonth && cell.utc >= today ? cell.day : null;
      if (target) return addInChat.current(target);
      const d = new Date(Date.now());
      if (d.getUTCFullYear() === year && d.getUTCMonth() + 1 === month) addInChat.current(d.getUTCDate());
      else if (Date.UTC(year, month - 1, 1) > today) addInChat.current(1);
    },
    [canAdd, month, today, year],
  );

  // --------------------------------------------------------------- keyboard
  const ordered = useMemo(
    () =>
      [...(view === "list" && filters.needsYou && queueLive ? [...matched, ...queuePosts.filter((p) => matchesFilters(p, filters))] : posts)].sort(
        (a, b) => Date.parse(a.slot.scheduledAt) - Date.parse(b.slot.scheduledAt) || a.slot.order - b.slot.order,
      ),
    [filters, matched, posts, queuePosts, view, queueLive],
  );

  const keyState = useRef({ step, goToday, setView, addOn, ordered, peek, showPeek });
  useEffect(() => {
    keyState.current = { step, goToday, setView, addOn, ordered, peek, showPeek };
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;
      // A dialog other than the peek owns the keys while it is open.
      if (document.querySelector(".co-modal-overlay")) return;
      // Escape puts a hover or focus peek away (a modal one closes itself).
      if (event.key === "Escape" && keyState.current.peek && peekNow.current.mode === "hover") {
        setPeek(null);
        return;
      }
      const k = keyState.current;
      const target = event.target instanceof HTMLElement ? event.target : null;
      // Inside a popover (the month picker, Channels, Pillars, settings) the
      // page keys stay quiet; inside the peek only J and K move between posts.
      const inPopover = !!target?.closest("[role='dialog'], [role='menu']");
      const inPeek = !!target?.closest("[data-calendar-peek]");
      if (inPopover && !(inPeek && (event.key === "j" || event.key === "k" || event.key === "J" || event.key === "K"))) return;
      const inGrid = event.target instanceof HTMLElement && !!event.target.closest("[role='grid'], [role='list'], [role='dialog'], [role='tablist']");
      const key = event.key.toLowerCase();
      if ((event.key === "ArrowLeft" || event.key === "ArrowRight") && !inGrid) {
        event.preventDefault();
        k.step(event.key === "ArrowRight" ? 1 : -1);
      } else if (key === "t") {
        k.goToday();
      } else if (key === "m") {
        k.setView("month");
      } else if (key === "w") {
        k.setView("week");
      } else if (key === "l") {
        k.setView("list");
      } else if (key === "s") {
        k.setView("schedule");
      } else if (key === "n") {
        event.preventDefault();
        k.addOn(focusedDay.current);
      } else if (key === "j" || key === "k") {
        const list = k.ordered;
        if (list.length === 0) return;
        const current =
          (k.peek?.kind === "post" ? k.peek.post.slot.id : null) ??
          (document.activeElement instanceof HTMLElement ? document.activeElement.dataset.postId : undefined);
        const index = list.findIndex((post) => post.slot.id === current);
        const next = list[index === -1 ? 0 : Math.max(0, Math.min(list.length - 1, index + (key === "j" ? 1 : -1)))];
        const node = document.querySelector<HTMLElement>(`[data-post-id="${next.slot.id}"]`);
        if (!node) return;
        node.focus();
        node.scrollIntoView({ block: "nearest" });
        if (k.peek) k.showPeek(next, node);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ------------------------------------------------------------------ status
  const channelName = filters.channel ? CHANNEL_NAME[filters.channel] : undefined;
  const pillarName = counts.pillars.find((entry) => entry.id === filters.pillarId)?.name;
  // Other months are read for the Needs you queue in List, and on a phone for
  // the schedule, which runs across months whether or not a filter is on.
  const queueWanted = view === "list" && (filters.needsYou || phone);
  const queueActive = view === "list" && filters.needsYou && queueLive;
  useEffect(() => {
    onQueueWanted?.(queueWanted);
  }, [onQueueWanted, queueWanted]);
  const listPosts = queueActive
    ? [...matched, ...queuePosts.filter((post) => matchesFilters(post, filters))]
    : matched;

  let status: ReactNode;
  let statusTone: "default" | "accent" | "alert" | "muted" = "default";
  if (state === "loading") {
    status = `Loading ${monthName}'s posts…`;
    statusTone = "muted";
  } else if (state === "error") {
    status = `${monthName} could not be loaded.`;
    statusTone = "alert";
  } else if (state === "no_strategy") {
    status = "No approved strategy yet, so there is nothing to plan from.";
  } else if (state === "not_planned") {
    const run = autoPlanRun(settings, year, month, now);
    status = run ? `Not planned yet. It plans itself on ${run.label}, ${run.ahead} ahead.` : "Not planned yet.";
  } else if (state === "planning") {
    statusTone = "accent";
    const target = monthTargets ? monthTargets.static + monthTargets.vertical + monthTargets.horizontal : 0;
    // Figma 08 (13 Oct): "Planning January · 0 of 5 placed · 0:04." then "Placing the posts…" and Stop planning.
    status = (
      <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>
          Planning {monthName} · {posts.length}
          {target > 0 ? ` of ${target}` : ""} placed
          <ElapsedSince since={planningSince} prefix=" · " />.
        </span>
        <span className="text-co-muted">{posts.length > 0 ? "Each post writes its three ideas as it lands." : "Placing the posts…"}</span>
        {onStopPlanning ? (
          <button
            type="button"
            onClick={onStopPlanning}
            className="text-[13px] font-medium text-co-text2 underline-offset-2 outline-none hover:text-co-text hover:underline focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            Stop planning
          </button>
        ) : null}
      </span>
    );
  } else if (state === "planning_failed") {
    statusTone = "alert";
    status = `Planning ${monthName} stopped${posts.length === 0 ? " before any post was placed" : ""}${planError ? `: ${planError.replace(/[.\s]+$/, "")}` : ""}.`;
  } else if (lift) {
    status = `Drop it on a day of ${monthName}. Esc puts it back.`;
  } else if (queueActive) {
    const total = listPosts.length;
    const months = listPosts.map((post) => post.slot.scheduledAt.slice(0, 7)).sort();
    const label = (key: string) => `${MONTH_NAMES[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
    const span =
      months.length === 0 ? "" : months[0] === months.at(-1) ? `, all in ${label(months[0])}` : `, ${label(months[0])} to ${label(months.at(-1)!)}`;
    status = total === 0 ? "Nothing needs you in the months around this one." : `${total} ${total === 1 ? "post needs" : "posts need"} you${span}.`;
  } else if (active) {
    status = filteredSentence(matched.length, posts.length, filters, {
      monthName,
      clientName,
      isGuest,
      view: view === "schedule" ? "list" : view,
      channelName,
      pillarName,
    });
  } else {
    status = closed
      ? closedSentence(posts)
      : statusSentence(posts, { clientName, isGuest });
    // Figma 08: "2 are with Fatafeat" opens Client proposals.
    if (typeof status === "string" && onOpenProposals && !isGuest) {
      const sentence = status;
      const match = /\d+ (?:is|are) with [^,.;]+/.exec(sentence);
      if (match) {
        status = (
          <>
            {sentence.slice(0, match.index)}
            <button type="button" onClick={onOpenProposals} className="underline decoration-co-line2 underline-offset-4 outline-none hover:text-co-text hover:decoration-co-text focus-visible:outline-2 focus-visible:outline-co-accent">
              {match[0]}
            </button>
            {sentence.slice(match.index + match[0].length)}
          </>
        );
      }
    }
    // More than one post waits for a pick: walk them, post to post.
    const waiting = posts
      .map((post) => post.slot)
      .filter((slot) => slot.origin !== "DIRECT" && awaitsPick(slot))
      .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt) || a.order - b.order);
    if (onPickIdeasWalk && canWrite && !isGuest && waiting.length > 1) {
      status = (
        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>{status}</span>
          <button type="button" onClick={() => onPickIdeasWalk(waiting[0]!)} className={stateLink}>
            Pick ideas for {waiting.length} posts
          </button>
        </span>
      );
    }
  }

  const showFilters = state === "ready" || state === "planning" || state === "loading";

  // Calendar / Plan review strip: a plan that landed in the last week, with
  // posts still waiting for their pick.
  const pipelineSlots = slots.filter((slot) => slot.origin !== "DIRECT" && slot.status !== "MISSED");
  const awaiting = pipelineSlots
    .filter((slot) => awaitsPick(slot))
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt) || a.order - b.order);
  const reviewStrip =
    planId && plannedAt && state === "ready" && canWrite && !isGuest && !closed && onPickIdeasWalk &&
    awaiting.length > 0 && now - Date.parse(plannedAt) < 7 * 86_400_000 ? (
      <PlanReviewStrip
        key={planId}
        planId={planId}
        plannedAt={plannedAt}
        now={now}
        picked={pipelineSlots.length - awaiting.length}
        total={pipelineSlots.length}
        onPickIdeas={() => onPickIdeasWalk(awaiting[0]!)}
      />
    ) : null;
  const title = view === "week" ? weekTitle(weekStart, span) : `${monthName} ${year}`;
  const settingsItems = [
    ...(brandId && settings && canEdit && !isGuest
      ? [{ label: "Calendar settings", onPress: () => setSettingsOpen(true) }]
      : []),
    ...(onSaveCadence && targets ? [{ label: `${monthName} scope`, onPress: () => setEditingScope(true) }] : []),
    ...menuItems,
  ];

  // -------------------------------------------------------------------- body
  const callbacks = {
    onOpenPost: openPost,
    peekFor,
    onOpenDay: openDay,
    onAddDay: (cell: DayCell) => addOn(cell),
    onMove: canWrite && state === "ready" ? onMoveSlot : undefined,
    onFocusDay: (cell: DayCell) => {
      focusedDay.current = cell;
    },
    onLiftChange: setLift,
  };
  const activePostId = livePeek?.kind === "post" ? livePeek.post.slot.id : null;

  const emptyTitle = filters.needsYou && !filters.withClient && !filters.channel && !filters.pillarId
    ? `Nothing needs you in ${monthName}`
    : filters.withClient && !filters.needsYou && !filters.channel && !filters.pillarId
      ? `Nothing is with ${other} in ${monthName}`
      : filters.channel && !filters.needsYou && !filters.withClient && !filters.pillarId
        ? `Nothing goes to ${channelName} in ${monthName}`
        : filters.pillarId && !filters.needsYou && !filters.withClient && !filters.channel
          ? `Nothing in ${pillarName ?? "that pillar"} in ${monthName}`
          : `Nothing matches the filters in ${monthName}`;

  // What the month holds instead, under an empty filter: "Two need you and three
  // are moving along; none is waiting on Fatafeat."
  const others = posts.length - counts.needsYou;
  const emptyBody =
    posts.length === 0
      ? undefined
      : `${capitalise(countWord(counts.needsYou))} ${counts.needsYou === 1 ? "needs" : "need"} you and ${countWord(others)} ${
          others === 1 ? "is" : "are"
        } moving along${filters.withClient && !filters.needsYou ? `; none is waiting on ${other}` : ""}.`;

  let body: ReactNode;
  if (state === "error") {
    body = (
      <CentreMessage
        title={`${monthName} could not be loaded`}
        body="The calendar did not answer. Nothing you did was lost."
        actions={
          onRetry ? (
            <button type="button" onClick={onRetry} className={statePrimary}>
              Try again
            </button>
          ) : null
        }
      />
    );
  } else if (view === "list" && state !== "loading") {
    body =
      listPosts.length === 0 ? (
        active ? (
          // Pen: List · a filter with nothing in it. The columns stay; the state
          // sits in what is left, 80 above the foot.
          <div className="flex flex-1 flex-col">
            {tablet ? null : <ListHeader />}
            <div className="flex flex-1 flex-col items-center justify-center pb-20">
              <EmptyState
                minHeight={0}
                className="w-[560px] max-w-full"
                icon={<FilterX className="size-5" aria-hidden="true" />}
                title={queueActive ? "Nothing needs you" : emptyTitle}
                body={queueActive ? undefined : emptyBody}
                actions={
                  <>
                    <StateAction onClick={() => setFilters(NO_FILTERS)}>Clear the filter</StateAction>
                    {!filters.needsYou && counts.needsYou > 0 ? (
                      <StateAction quiet onClick={() => setFilters({ ...NO_FILTERS, needsYou: true })}>
                        Show what needs you
                      </StateAction>
                    ) : null}
                  </>
                }
              />
            </div>
          </div>
        ) : (
          <CentreMessage title={`No posts in ${monthName} yet`} />
        )
      ) : tablet ? (
        <PhoneSchedule
          months={phoneSchedule(listPosts, { fromUtc: Number.NEGATIVE_INFINITY, todayUtc: today }).months}
          bannerFor={(group, index) =>
            queueActive || index > 0 || group.year !== year || group.month !== month
              ? `${MONTH_NAMES[group.month - 1]} ${group.year}`
              : null
          }
          quiet={(post) => closed || todayOf(Date.parse(post.slot.scheduledAt)) < today}
          activePostId={activePostId}
          onOpen={openPost}
        />
      ) : (
        <MonthList
          posts={queueActive ? listPosts : posts}
          shown={queueActive ? undefined : new Set(matched.map((post) => post.slot.id))}
          isGuest={isGuest}
          now={now}
          activePostId={activePostId}
          showMonths={queueActive}
          onOpen={openPost}
          peekFor={peekFor}
          onDecideRisk={canWrite && !isGuest ? onDecideRisk : undefined}
        />
      );
  } else if (view === "week") {
    body = (
      <WeekGrid
        year={year}
        month={month}
        monthName={monthName}
        weekStart={weekStart}
        days={span}
        posts={posts}
        neighbourPosts={neighbourPosts}
        filters={filters}
        todayUtc={today}
        closed={closed}
        canAdd={canAdd}
        isGuest={isGuest}
        clientName={clientName}
        activePostId={activePostId}
        callbacks={callbacks}
      />
    );
  } else {
    body = (
      <MonthGrid
        year={year}
        month={month}
        monthName={monthName}
        posts={state === "loading" ? [] : posts}
        filters={filters}
        todayUtc={today}
        closed={closed}
        canAdd={canAdd}
        clientName={clientName}
        callbacks={callbacks}
        skeleton={state === "loading"}
        oneLine={tablet}
      />
    );
  }

  // Calendar / Scope inside the not-planned card: "Static 1", "Vertical video 2".
  const scopeItems = monthTargets
    ? [
        monthTargets.static ? `Static ${monthTargets.static}` : null,
        monthTargets.vertical ? `Vertical video ${monthTargets.vertical}` : null,
        monthTargets.horizontal ? `Horizontal video ${monthTargets.horizontal}` : null,
      ].filter((item): item is string => !!item)
    : [];
  const planCount = monthTargets ? monthTargets.static + monthTargets.vertical + monthTargets.horizontal : 0;

  // The scope dialog's "N planned" per format; a quick post counts toward the month on its own.
  const plannedCounts = useMemo(() => {
    const counts = { staticPostsPerMonth: 0, verticalVideoPostsPerMonth: 0, horizontalVideoPostsPerMonth: 0, quick: 0 };
    for (const slot of slots) {
      if (slot.origin === "DIRECT") counts.quick += 1;
      else if (slot.mediaType === "STATIC") counts.staticPostsPerMonth += 1;
      else if (slot.orientation === "HORIZONTAL") counts.horizontalVideoPostsPerMonth += 1;
      else counts.verticalVideoPostsPerMonth += 1;
    }
    return counts;
  }, [slots]);
  // "The quick post on Fri 11 Dec makes December 6 of 5." when quick posts tip the month over.
  const quickLine = useMemo(() => {
    if (!monthTargets || plannedCounts.quick === 0) return null;
    const target = monthTargets.static + monthTargets.vertical + monthTargets.horizontal;
    const all =
      plannedCounts.staticPostsPerMonth + plannedCounts.verticalVideoPostsPerMonth + plannedCounts.horizontalVideoPostsPerMonth + plannedCounts.quick;
    if (all <= target) return null;
    const quick = slots.filter((slot) => slot.origin === "DIRECT");
    const day = quick.length === 1 ? shortDate(quick[0].scheduledAt) : null;
    return quick.length === 1
      ? `The quick post${day ? ` on ${day}` : ""} makes ${monthName} ${all} of ${target}.`
      : `The ${quick.length} quick posts make ${monthName} ${all} of ${target}.`;
  }, [monthTargets, plannedCounts, slots, monthName]);

  // "December drops to 4 of 5 posts: 0 static." What deleting a post leaves of the month.
  const monthAfterDelete = (slot: CalendarPlanSlot): string | null => {
    if (slot.origin === "DIRECT" || !scope) return null;
    const key = slot.mediaType === "STATIC" ? "static" : slot.orientation === "HORIZONTAL" ? "horizontal" : "vertical";
    const line = scope.find((entry) => entry.key === key);
    const left = slots.filter((s) => s.id !== slot.id && s.origin !== "DIRECT").length;
    // Figma 08: "December drops to 4 of 5 posts: 0 static."
    const target = monthTargets ? monthTargets.static + monthTargets.vertical + monthTargets.horizontal : null;
    return `${monthName} drops to ${left}${target !== null ? ` of ${target}` : ""} ${left === 1 && target === null ? "post" : "posts"}${
      line ? `: ${Math.max(0, line.placed - 1)} ${line.label.toLowerCase()}` : ""
    }.`;
  };

  // A post's own actions, the same rules as the peek's ⋯.
  const movable = (post: CalendarPost) =>
    canWrite && state === "ready" && !!onMoveSlot && ownMonth(post.slot) && post.slot.status !== "IN_CLIENT_REVIEW";
  const deletable = (post: CalendarPost) => canWrite && !!onDeleteSlot && ownMonth(post.slot);
  const actionsOf = (post: CalendarPost) =>
    movable(post) || deletable(post) ? () => setActionsFor(post) : undefined;

  const dialogs = (
    <>
    {adding !== null && onAddPost ? (
      <AddPostDialog
        year={year}
        month={month}
        day={adding}
        pillars={pillars}
        phone={phone}
        onAdd={onAddPost}
        onQuick={
          onQuickPost
            ? (day) => {
                setAdding(null);
                onQuickPost(day);
              }
            : undefined
        }
        onClose={() => setAdding(null)}
      />
    ) : null}

    {moving && onMoveSlot ? (
      <MovePostDialog
        slot={moving}
        slots={slots}
        year={year}
        month={month}
        onMove={onMoveSlot}
        onClose={() => setMoving(null)}
        phone={phone}
        posts={posts}
        clientName={clientName}
        closed={closed}
      />
    ) : null}

    {deleting && onDeleteSlot ? (
      <DeleteSlotModal
        slot={deleting}
        brief={briefBySlot?.get(deleting.id) ?? null}
        clientName={clientName}
        phone={phone}
        loading={false}
        monthAfter={monthAfterDelete(deleting)}
        onConfirm={() => {
          // Optimistic: the post leaves on confirm; the mutation rolls back and toasts on failure.
          const id = deleting.id;
          setDeleting(null);
          void Promise.resolve(onDeleteSlot(id)).catch(() => undefined);
        }}
        onClose={() => setDeleting(null)}
      />
    ) : null}

    {editingScope && onSaveCadence && monthTargets ? (
      // Figma 08, "December scope": the month's own numbers against what it holds.
      <CadenceDialog
        scope="month"
        title={`${monthName} scope`}
        description={`Set for ${monthName} only. The brand’s settings stay as they are.`}
        monthName={monthName}
        initial={{
          staticPostsPerMonth: monthTargets.static,
          verticalVideoPostsPerMonth: monthTargets.vertical,
          horizontalVideoPostsPerMonth: monthTargets.horizontal,
        }}
        planned={plannedCounts}
        quickLine={quickLine}
        brandLine={
          settings
            ? `Other months start from ${clientName?.trim() ? `${clientName.trim()}’s` : "the brand’s"} ${
                settings.staticPostsPerMonth + settings.verticalVideoPostsPerMonth + settings.horizontalVideoPostsPerMonth
              } a month, in the calendar settings.`
            : undefined
        }
        saving={savingCadence}
        onSave={async (draft) => {
          try {
            await onSaveCadence(draft);
            setEditingScope(false);
          } catch {
            // The mutation owns the error toast. Keep the draft in place.
          }
        }}
        onReset={
          hasCustomCadence && onResetCadence
            ? async () => {
                try {
                  await onResetCadence();
                  setEditingScope(false);
                } catch {
                  // Same contract: the mutation toasts, the editor stays open.
                }
              }
            : undefined
        }
        onReplan={
          onReplan
            ? (changed) => {
                setEditingScope(false);
                onReplan(changed);
              }
            : undefined
        }
        onClose={() => setEditingScope(false)}
      />
    ) : null}

    {settingsOpen && brandId && settings ? (
      <CalendarSettingsModal
        brandId={brandId}
        settings={settings}
        brandName={clientName}
        viewport={viewport}
        onClose={() => setSettingsOpen(false)}
      />
    ) : null}
      {pickerOpen && onPickMonth ? (
        <MonthPickerSheet
          picker={{ year, month, monthsWithPosts: monthsWithPosts ?? new Set(), onPick: onPickMonth, onSeeYear }}
          posts={posts}
          chosenDay={chosenState?.key === monthKey ? chosenState.day : defaultChosenDay(year, month, posts, today)}
          onPickDay={(day) => {
            // Month: the day is chosen; List: the schedule starts at it.
            setChosen({ key: monthKey, day });
            setScheduleFrom({ key: monthKey, utc: Date.UTC(year, month - 1, day) });
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}

      {actionsFor ? (
        <PostActionsSheet
          post={actionsFor}
          onMove={
            movable(actionsFor)
              ? () => {
                  setActionsFor(null);
                  setMoving(actionsFor.slot);
                }
              : undefined
          }
          onDelete={
            deletable(actionsFor)
              ? () => {
                  setActionsFor(null);
                  setDeleting(actionsFor.slot);
                }
              : undefined
          }
          deleteBlocked={slotDeleteBlocked(actionsFor.slot)}
          onClose={() => setActionsFor(null)}
        />
      ) : null}
    </>
  );

  if (view === "schedule") {
    const openMonth = () => (phone ? setPhoneView("list") : setView("month"));
    const openScheduled = (post: UpcomingPost) => onOpenScheduled?.(post);
    if (phone) {
      return (
        <div className="relative flex min-h-[calc(100dvh-66px)] w-full min-w-0 flex-col">
          <CalendarTopLane
            brandName={clientName}
            onAdd={canAdd ? () => addOn(null) : undefined}
            addLabel="Add a post"
            settings={settingsItems}
            bell={notificationBell}
            views={{ value: phoneView, options: PHONE_VIEWS, onChange: (next) => setPhoneView(next as PhoneCalendarLayout) }}
          />
          <ScheduleView
            brandId={brandId}
            layout="phone"
            onOpenPost={openScheduled}
            onOpenMonth={openMonth}
            renderHeader={({ status: line, chips }) => (
              <>
                <header className="flex flex-col gap-1.5 px-1 pb-3 pt-4">
                  <PhoneLargeTitle title={monthName} onPick={onPickMonth ? () => setPickerOpen(true) : undefined} />
                  <p role="status" aria-live="polite" className="text-[14px] leading-[1.45] text-co-text2">
                    {line}
                  </p>
                </header>
                {chips}
              </>
            )}
          />
          {dialogs}
        </div>
      );
    }
    return (
      <div
        className={cn("relative flex min-w-0 flex-col", tablet ? "min-h-[calc(100dvh-66px)] w-full" : "co-cal-page")}
        data-view={view}
        data-cal-root=""
      >
        {tablet ? (
          <CalendarTopLane tablet brandName={clientName} onAdd={canAdd ? () => addOn(null) : undefined} settings={settingsItems} />
        ) : null}
        <ScheduleView
          brandId={brandId}
          layout={tablet ? "tablet" : "desktop"}
          onOpenPost={openScheduled}
          onOpenMonth={openMonth}
          renderHeader={({ status: line, filters: own }) => (
            <CalendarHeader
              layout={tablet ? "tablet" : "desktop"}
              title={`${monthName} ${year}`}
              weekLabel={tablet ? "3 days" : "Week"}
              view={view}
              onViewChange={setView}
              onToday={goToday}
              onPrev={() => step(-1)}
              onNext={() => step(1)}
              prevLabel="Previous month"
              nextLabel="Next month"
              arrows={false}
              picker={
                onPickMonth
                  ? { year, month, monthsWithPosts: monthsWithPosts ?? new Set(), onPick: onPickMonth, onSeeYear }
                  : null
              }
              status={line}
              filterRow={own}
              settings={tablet ? [] : settingsItems}
            />
          )}
        />
        {dialogs}
      </div>
    );
  }

  if (phone) {
    const phoneStatusColor =
      statusTone === "accent"
        ? "var(--co-accent-ink)"
        : statusTone === "alert"
          ? "var(--co-alert-ink)"
          : statusTone === "muted"
            ? "var(--co-muted)"
            : "var(--co-text2)";
    const scheduleFrom =
      scheduleFromState?.key === monthKey ? scheduleFromState.utc : scheduleStart(year, month, today);
    const chosenDay =
      chosenState?.key === monthKey ? chosenState.day : defaultChosenDay(year, month, posts, today);
    const chosenCell = sixWeekGrid(year, month).find((cell) => cell.inMonth && cell.day === chosenDay) ?? null;
    const quiet = (post: CalendarPost) => closed || todayOf(Date.parse(post.slot.scheduledAt)) < today;
    const open = (post: CalendarPost) => onOpenSlot(post.slot);
    const everyPost = queueLive ? [...posts, ...queuePosts] : posts;
    const schedule = phoneSchedule(
      everyPost.filter((post) => matchesFilters(post, filters)),
      { fromUtc: scheduleFrom, todayUtc: today },
    );
    const scheduled = schedule.months.reduce((n, group) => n + group.days.reduce((m, d) => m + d.posts.length, 0), 0);
    // A filter other than Needs you on the phone's List reads every month the
    // schedule holds, so its sentence counts what the list shows, not the month.
    const acrossMonths = view === "list" && active && !filters.needsYou && state === "ready";
    let phoneStatus: ReactNode = status;
    if (acrossMonths) {
      const one = scheduled === 1 || scheduled === 0;
      const count = [filters.withClient, !!filters.channel, !!filters.pillarId].filter(Boolean).length;
      const phrase =
        count > 1
          ? one ? "matches the filters" : "match the filters"
          : filters.withClient
            ? `${one ? "is" : "are"} with ${other}`
            : filters.channel
              ? `${one ? "goes" : "go"} to ${channelName ?? "that channel"}`
              : `${one ? "is" : "are"} in ${pillarName ?? "that pillar"}`;
      const shown = schedule.months.filter((group) => group.days.some((d) => d.posts.length > 0));
      const label = (group: { year: number; month: number }) => `${MONTH_NAMES[group.month - 1]} ${group.year}`;
      const from = new Date(scheduleFrom);
      const fromLabel = dayMonthShort({ weekday: from.getUTCDay(), day: from.getUTCDate(), month: from.getUTCMonth() + 1 });
      phoneStatus =
        scheduled === 0
          ? `No post ${phrase} from ${fromLabel} on.`
          : `${scheduled} ${scheduled === 1 ? "post" : "posts"} ${phrase}, ${
              shown.length === 1 ? `all in ${label(shown[0])}` : `${label(shown[0])} to ${label(shown.at(-1)!)}`
            }.`;
    }
    const planLine = onPlan && canWrite ? (
      <button type="button" onClick={onPlan} disabled={planPending} className={phonePrimary}>
        Plan {monthName}
        <Sparkles className="size-4" aria-hidden="true" />
      </button>
    ) : null;
    const addLine = canAdd ? (
      <button type="button" onClick={() => addOn(null)} className={phoneSecondary}>
        Add post
      </button>
    ) : null;
    const clearFilter = (
      <button type="button" onClick={() => setFilters(NO_FILTERS)} className={phoneSecondary}>
        Clear the filter
      </button>
    );

    let phoneBody: ReactNode;
    if (state === "error") {
      phoneBody = (
        <CentreMessage
          title={`${monthName} could not be loaded`}
          body="The calendar did not answer. Nothing you did was lost."
          actions={
            onRetry ? (
              <button type="button" onClick={onRetry} className={phonePrimary}>
                Try again
              </button>
            ) : null
          }
        />
      );
    } else if (state === "no_strategy") {
      phoneBody = (
        <PhoneStateCard
          title="The calendar plans from a strategy"
          body={`Posts are placed from the strategy’s pillars and scope. Approve ${isGuest ? "the" : `${clientTitle(clientName)}’s`} strategy and the calendar can plan its first month.`}
          actions={
            <>
              {addLine}
              <button type="button" onClick={() => navigate(strategyHref)} className={phonePrimary}>
                Open Strategy
                <ArrowRight className="size-4" aria-hidden="true" />
              </button>
            </>
          }
        />
      );
    } else if (state === "not_planned" && view === "list") {
      phoneBody = (
        <PhoneStateCard
          title={`${monthName} isn’t planned yet`}
          body={`The agent places ${planCount ? `${countWord(planCount)} ${planCount === 1 ? "post" : "posts"}` : "the posts"} from ${
            isGuest ? "the" : `${clientTitle(clientName)}’s`
          } strategy, then writes three ideas for each. You can move, add or remove posts afterwards.`}
          counts={
            monthTargets
              ? [
                  { count: monthTargets.static, kind: "static" },
                  { count: monthTargets.vertical, kind: "vertical" },
                  { count: monthTargets.horizontal, kind: "horizontal" },
                ].filter((entry) => entry.count > 0)
              : undefined
          }
          actions={
            addLine || planLine ? (
              <>
                {addLine}
                {planLine}
              </>
            ) : null
          }
        />
      );
    } else if (view === "list") {
      phoneBody =
        state === "loading" ? (
          <PhoneScheduleSkeleton />
        ) : scheduled === 0 && active ? (
          <CentreMessage
            className="min-h-[360px]"
            title={queueActive ? "Nothing needs you" : emptyTitle.replace(` in ${monthName}`, " from here on")}
            actions={
              <>
                {schedule.earlier > 0 ? (
                  <button
                    type="button"
                    onClick={() => setScheduleFrom({ key: monthKey, utc: monthBefore(scheduleFrom) })}
                    className={phoneSecondary}
                  >
                    Earlier
                  </button>
                ) : null}
                {clearFilter}
              </>
            }
          />
        ) : scheduled === 0 && schedule.earlier === 0 ? (
          <CentreMessage className="min-h-[360px]" title={`No posts from ${monthName} on yet`} actions={addLine} />
        ) : (
          <PhoneSchedule
            months={schedule.months}
            bannerFor={(group, index) =>
              index === 0 && group.year === year && group.month === month
                ? null
                : `${MONTH_NAMES[group.month - 1]}${group.year !== new Date(today).getUTCFullYear() ? ` ${group.year}` : ""}`
            }
            quiet={quiet}
            earlier={schedule.earlier}
            onEarlier={() => setScheduleFrom({ key: monthKey, utc: monthBefore(scheduleFrom) })}
            onOpen={open}
            actionsFor={actionsOf}
          />
        );
    } else {
      const chosenKey = dayKey(year, month, chosenDay);
      const byDay = postsByDay(matched);
      const onChosen = byDay.get(chosenKey) ?? [];
      const laterDays = [...byDay.entries()]
        .filter(([key]) => key > chosenKey)
        .sort(([a], [b]) => a.localeCompare(b));
      const row = (post: CalendarPost) => {
        const d = new Date(post.slot.scheduledAt);
        return (
          <PhoneDayRow
            key={post.slot.id}
            post={post}
            day={d.getUTCDate()}
            weekday={d.getUTCDay()}
            quiet={quiet(post)}
            onOpen={() => open(post)}
            onActions={actionsOf(post)}
          />
        );
      };
      phoneBody = (
        <div className="flex flex-col">
          <div className="-mx-3 px-3 shadow-[inset_0_-1px_0_var(--co-line)]">
            <CompactMonth
              days={compactMonthDays(year, month, state === "loading" ? [] : matched, { todayUtc: today, chosenKey })}
              label={`${monthName} ${year}`}
              onChoose={(d) => setChosen({ key: monthKey, day: d.cell.day })}
            />
          </div>
          <div>
            {state === "loading" ? (
              <PhoneScheduleSkeleton />
            ) : (
              <>
                <PhoneGroupHeader>{chosenCell ? dayMonthShort(chosenCell) : monthName}</PhoneGroupHeader>
                {onChosen.length > 0 ? (
                  onChosen.map(row)
                ) : (
                  <p className="-mx-3 px-4 py-3.5 text-[13px] text-co-muted shadow-[inset_0_-1px_0_var(--co-line)]">
                    {active ? "Nothing on this day matches the filter." : "Nothing on this day."}
                  </p>
                )}
                {laterDays.length > 0 ? (
                  <>
                    <PhoneGroupHeader>Later in {monthName}</PhoneGroupHeader>
                    {laterDays.flatMap(([, list]) => list.map(row))}
                  </>
                ) : null}
                {state === "not_planned" && (planLine || addLine) ? (
                  <div className="grid grid-cols-2 gap-2.5 pt-5">
                    {addLine ?? <span />}
                    {planLine}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={cn("relative flex w-full min-w-0 flex-col", state === "not_planned" || state === "no_strategy" ? "pb-28" : "pb-10")}>
        <CalendarTopLane
          brandName={clientName}
          onAdd={canAdd ? () => addOn(view === "month" ? chosenCell : null) : undefined}
          addLabel={view === "month" && chosenCell ? `Add a post on ${dayMonthShort(chosenCell)}` : "Add a post"}
          settings={settingsItems}
          bell={notificationBell}
          views={{
            value: phoneView,
            options: PHONE_VIEWS,
            onChange: (next) => setPhoneView(next as PhoneCalendarLayout),
          }}
        />
        {/* Bar / Large title · Phone: pad 16/16/12, gap 6. */}
        <header className="flex flex-col gap-1.5 px-1 pb-3 pt-4">
          <PhoneLargeTitle title={monthName} onPick={onPickMonth ? () => setPickerOpen(true) : undefined} />
          <p role="status" aria-live="polite" className="text-[14px] leading-[1.45]" style={{ color: phoneStatusColor }}>
            {phoneStatus}
          </p>
        </header>
        {showFilters ? (
          // Filter chip · Phone: All, Needs you and With the client, 34 tall, 8 apart.
          <div className="-mx-3 flex items-center gap-2 overflow-x-auto px-4 pb-3 [scrollbar-width:none]">
            <PhoneChip
              label="All"
              count={posts.length}
              on={!active}
              onPress={() => setFilters(NO_FILTERS)}
            />
            <PhoneChip
              label="Needs you"
              count={counts.needsYou}
              on={filters.needsYou}
              onPress={() => setFilters({ ...NO_FILTERS, needsYou: !filters.needsYou })}
            />
            <PhoneChip
              label={isGuest ? "With the team" : `With ${clientLabel(clientName)}`}
              count={counts.withClient}
              on={filters.withClient}
              onPress={() => setFilters({ ...NO_FILTERS, withClient: !filters.withClient })}
            />
          </div>
        ) : null}
        {notices ? <div className="flex flex-col gap-3 pb-4">{notices}</div> : null}
        {phoneBody}
        {dialogs}
      </div>
    );
  }

  return (
    <div
      className={cn("relative flex min-w-0 flex-col", tablet ? "min-h-[calc(100dvh-66px)] w-full" : "co-cal-page")}
      data-view={view}
      data-cal-root=""
    >
      {tablet ? (
        <CalendarTopLane
          tablet
          brandName={clientName}
          onAdd={canAdd ? () => addOn(focusedDay.current) : undefined}
          settings={settingsItems}
        />
      ) : null}
      <CalendarHeader
        layout={tablet ? "tablet" : "desktop"}
        title={title}
        compactTitle={view === "week" ? weekTitle(weekStart, span) : `${monthName} ${year}`}
        weekLabel={tablet ? "3 days" : "Week"}
        view={view}
        onViewChange={setView}
        onToday={goToday}
        onPrev={() => step(-1)}
        onNext={() => step(1)}
        prevLabel={view === "week" ? "Previous week" : "Previous month"}
        nextLabel={view === "week" ? "Next week" : "Next month"}
        picker={
          onPickMonth
            ? { year, month, monthsWithPosts: monthsWithPosts ?? new Set(), onPick: onPickMonth, onSeeYear }
            : null
        }
        status={status}
        statusTone={statusTone}
        filters={
          showFilters
            ? { counts, value: filters, onChange: setFilters, otherLabel: isGuest ? "With the team" : `With ${clientLabel(clientName)}`, monthName, closed }
            : null
        }
        settings={tablet ? [] : settingsItems}
      />

      {staleSince ? (
        <ConnectionBanner monthName={monthName} since={staleSince} onRetry={onRetry} className={tablet ? "mb-4" : undefined} />
      ) : null}
      {notices ? <div className={cn("flex flex-col gap-3 pb-4", !tablet && "px-8")}>{notices}</div> : null}
      {reviewStrip && view !== "list" ? <div className={cn("pb-4", !tablet && "px-8")}>{reviewStrip}</div> : null}

      <div className={cn("relative flex min-h-0 min-w-0 flex-1 flex-col", !tablet && "px-8", view === "list" && "pb-10")}>
        {body}

        {view !== "list" && state === "not_planned" ? (
          <CentreCard
            title={`${monthName} isn’t planned yet`}
            body={
              <>
                The agent places {planCount ? `${countWord(planCount)} ${planCount === 1 ? "post" : "posts"}` : "the posts"} from{" "}
                {isGuest ? "the" : `${clientTitle(clientName)}’s`} strategy, then writes three ideas for each. You can move,
                add or remove posts afterwards.
              </>
            }
            detail={scopeItems.length > 0 ? <ScopeLine title="What gets planned" items={scopeItems} /> : null}
            actions={
              <>
                {canAdd ? (
                  <button type="button" onClick={() => addOn(null)} className={stateSecondary}>
                    Add post
                  </button>
                ) : null}
                {onPlan && canWrite ? (
                  <button type="button" onClick={onPlan} disabled={planPending} className={statePrimary}>
                    Plan {monthName}
                    <Sparkles className="size-3.5" aria-hidden="true" />
                  </button>
                ) : null}
              </>
            }
          />
        ) : null}

        {view !== "list" && state === "planning_failed" && posts.length === 0 ? (
          <CentreCard
            title={`${monthName} didn’t get planned`}
            body={`The agent stopped before placing any post. Nothing was lost.${planError ? ` ${planError.replace(/[.\s]+$/, "")}.` : ""}`}
            actions={
              <>
                {canAdd ? (
                  <button type="button" onClick={() => addOn(null)} className={stateSecondary}>
                    Add posts
                  </button>
                ) : null}
                {onPlan && canWrite ? (
                  <button type="button" onClick={onPlan} disabled={planPending} className={statePrimary}>
                    Try again
                    <RotateCcw className="size-3.5" aria-hidden="true" />
                  </button>
                ) : null}
              </>
            }
          />
        ) : null}

        {view !== "list" && state === "no_strategy" ? (
          <CentreCard
            title="The calendar plans from a strategy"
            body={`Posts are placed from the strategy’s pillars and scope. Approve ${isGuest ? "the" : `${clientTitle(clientName)}’s`} strategy and the calendar can plan its first month.`}
            actions={
              <>
                {canAdd ? (
                  <button type="button" onClick={() => addOn(null)} className={stateSecondary}>
                    Add post
                  </button>
                ) : null}
                <button type="button" onClick={() => navigate(strategyHref)} className={statePrimary}>
                  Open Strategy
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </button>
              </>
            }
          />
        ) : null}
      </div>

      {view === "month" && state !== "error" && state !== "no_strategy" ? (
        <CalendarScope
          monthName={monthName}
          lines={
            state === "ready"
              ? scopeSummaryLines(slots, monthTargets, closed ? null : posts.filter((post) => post.state.turn.owner === "late").length)
              : scope
          }
          counting={state === "loading"}
          otherName={Other}
          showKey={posts.length > 0 && !closed}
          // Scope changes happen in the chat; a month not planned yet has no Change.
          onEdit={onSaveCadence && monthTargets && canEdit && state !== "not_planned" ? () => setEditingScope(true) : undefined}
        />
      ) : null}

      <PostPeek
        triggerRef={peekAnchor}
        placement={view === "list" ? "bottom start" : "end top"}
        content={livePeek}
        isGuest={isGuest}
        nonModal={peekMode === "hover"}
        onPointerEnter={peekIntent.hold}
        onReturnToCard={() => peekAnchor.current?.focus()}
        onFocusLeave={(next) => {
          if (peekMode === "hover" && next !== peekAnchor.current) setPeek(null);
        }}
        onPointerLeave={peekMode === "hover" ? peekIntent.leave : undefined}
        onClose={() => setPeek(null)}
        onOpenPost={openPost}
        onPickPost={openPost}
        primaryFor={primaryFor}
        onPrimary={runPrimary}
        onMove={
          canWrite && onMoveSlot && livePeek?.kind === "post" && ownMonth(livePeek.post.slot) && livePeek.post.slot.status !== "IN_CLIENT_REVIEW"
            ? (post) => {
                setPeek(null);
                setMoving(post.slot);
              }
            : undefined
        }
        deleteBlocked={(post) => slotDeleteBlocked(post.slot)}
        onDelete={
          canWrite && onDeleteSlot && livePeek?.kind === "post" && ownMonth(livePeek.post.slot)
            ? (post) => {
                setPeek(null);
                setDeleting(post.slot);
              }
            : undefined
        }
      />

      {dialogs}
    </div>
  );
}

/** A phone's views, List first. */
const PHONE_VIEWS: { label: string; value: PhoneCalendarLayout }[] = [
  { label: "List", value: "list" },
  { label: "Month", value: "month" },
  { label: "Next 14 days", value: "schedule" },
];

const COUNT_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

/** "five" up to ten, then digits. */
function countWord(count: number): string {
  return COUNT_WORDS[count] ?? String(count);
}


/** "10:40", the brand-agnostic local clock of the last good read. */
function clockOf(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Status banner · connection dropped: the posts on screen are the last good
 * read. Tonal, radius 16, a 36px tonal icon well, title 16/600, meta 13.5.
 */
function ConnectionBanner({
  monthName,
  since,
  onRetry,
  className,
}: {
  monthName: string;
  since: number;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div role="status" className={cn("flex min-h-[84px] items-center gap-4 rounded-panel bg-[var(--co-tonal)] px-5", className)}>
      <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-[4px] bg-[var(--co-tonal)]">
        <CloudOff className="size-[17px] text-co-alert-ink" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[16px] font-semibold text-co-text">
          Showing {monthName} as of {clockOf(since)}
        </span>
        <span className="text-[13.5px] leading-[1.4] text-co-text2">
          The connection dropped, so this may be out of date. Nothing you did was lost. It keeps trying.
        </span>
      </span>
      {onRetry ? (
        <button type="button" onClick={onRetry} className={statePrimary}>
          Try again
          <RotateCcw className="size-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/** Filter chip · Phone (and · On): a 34px control-glass capsule; on, the accent tint, border and check. */
function PhoneChip({ label, count, on, onPress }: { label: string; count: number; on: boolean; onPress: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onPress}
      className={cn(
        "co-glass-control inline-flex h-[34px] shrink-0 items-center gap-1.5 rounded-pill px-3 text-[13px] outline-none",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
        on
          ? "!bg-co-accent-dim font-semibold text-co-text !shadow-[inset_0_0_0_1px_var(--co-accent-border)]"
          : "font-medium text-co-text2",
      )}
    >
      {on ? <Check className="size-3.5 text-co-accent" strokeWidth={2.25} aria-hidden="true" /> : null}
      {label}
      <span className={cn("text-[12px] font-normal tabular-nums", on ? "text-co-accent" : "text-co-muted")}>{count}</span>
    </button>
  );
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
